import { industryNavigationItems } from '../../shared/content/industryPages.js';
import { html } from 'lit';
import { brandAssetRepository } from '@vivaporta/brand';
import { languageToggle } from '../components/languageToggle.js';
import {
  applicationPortal,
  type LandingContent,
  type LandingLocale,
  type LandingNavItem,
} from '../../shared/content/landingContent.js';
import type { StaticPageAudience } from '../../shared/content/staticPageContent.js';
import {
  editorNodeClass,
  editorNodeClick,
  editorNodePointerDown,
  editorNodeStyle,
  type LandingEditorRenderOptions,
} from '../editor/landingEditorRender.js';
import { navigationAboutItems } from './navigationAboutItems.js';
import { industryMegaMenu } from './industryMegaMenu.js';
import { localizedLandingHref } from '../../shared/landingDocumentUrl.js';
import { mobileNavigation, closeMobileNavigation } from './mobileNavigation.js';
// Direct contact details surfaced in the company "Fachkräfte anfragen" modal.
const companyContact = {
  email: 'info@vivaporta.org',
  phone: '+49 176 42887719',
  phoneHref: 'tel:+4917642887719',
};

export const openCompanyContactDialog = (root?: ParentNode | null) => {
  if (typeof document === 'undefined') return;
  const dialog =
    root?.querySelector<HTMLDialogElement>('.vp-contact-modal') ??
    document.querySelector<HTMLDialogElement>('.vp-navigation .vp-contact-modal');
  if (!dialog) return;
  if (typeof dialog.showModal === 'function') {
    if (!dialog.open) dialog.showModal();
  } else {
    dialog.setAttribute('open', '');
  }
  document.querySelector('.vp-page')?.classList.add('vp-scroll-locked');
};

interface NavigationOptions {
  content: LandingContent;
  currentLocale: LandingLocale;
  onLanguageChange: (locale: LandingLocale) => void;
  audience?: StaticPageAudience;
  onAudienceChange?: (audience: StaticPageAudience) => void;
  editor?: LandingEditorRenderOptions;
}

interface NavigationWorkItem extends LandingNavItem {
  text?: string;
}

class LandingNavigationViewModel {
  private static outsideClickCleanup?: () => void;
  private static readonly audienceStorageKey = 'vp-navigation-audience';

  constructor(private readonly options: NavigationOptions) {}

  render() {
    const { content, currentLocale, onLanguageChange, editor } = this.options;

    return html`
      <header
        class=${editorNodeClass(editor, 'navigation.container', 'vp-navigation')}
        style=${editorNodeStyle(editor, 'navigation.container')}
        @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, 'navigation.container', event)}
        @click=${(event: MouseEvent) => editorNodeClick(editor, 'navigation.container', event)}
      >
        <div class="vp-info-bar">
          <div class="vp-navigation-shell vp-info-bar-inner">
            <span
              class=${editorNodeClass(editor, 'navigation.eyebrow')}
              style=${editorNodeStyle(editor, 'navigation.eyebrow')}
              @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, 'navigation.eyebrow', event)}
              @click=${(event: MouseEvent) => editorNodeClick(editor, 'navigation.eyebrow', event)}
              >${content.hero.eyebrow}</span
            >
            <div class="vp-info-actions">
              <div class="vp-audience-switch" aria-label=${this.audienceLabel}>
                <a
                  class=${this.audienceClass('applicant')}
                  href=${this.currentAudienceHref}
                  data-audience="applicant"
                  @click=${(event: MouseEvent) => this.handleAudienceSwitch(event)}
                >
                  <span class="icon-[material-symbols--person-check]"></span>${this.applicantLabel}
                </a>
                <a
                  class=${this.audienceClass('company')}
                  href=${this.currentAudienceHref}
                  data-audience="company"
                  @click=${(event: MouseEvent) => this.handleAudienceSwitch(event)}
                >
                  <span class="icon-[material-symbols--business-center]"></span>${this.companyLabel}
                </a>
              </div>
              ${languageToggle({ currentLocale, onChange: onLanguageChange })}
            </div>
          </div>
        </div>

        <div class="vp-main-nav">
          <div class="vp-navigation-shell vp-main-nav-inner">
            <a
              class="vp-brand"
              href="/"
              aria-label="Viva Porta"
              @click=${(event: MouseEvent) => this.handleEditorLinkClick(event, '/')}
            >
              <img
                class=${editorNodeClass(editor, 'navigation.logo')}
                style=${editorNodeStyle(editor, 'navigation.logo')}
                src=${brandAssetRepository.navbarLogoDataUri}
                alt="Viva Porta"
                @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, 'navigation.logo', event)}
                @click=${(event: MouseEvent) => editorNodeClick(editor, 'navigation.logo', event)}
              />
            </a>

            <nav
              class="vp-nav-links"
              aria-label=${currentLocale === 'tr'
                ? 'Ana menü'
                : currentLocale === 'en'
                  ? 'Main navigation'
                  : 'Hauptnavigation'}
            >
              <details class="vp-nav-dropdown vp-nav-dropdown--industries" @toggle=${(event: Event) => this.closeSiblingDropdowns(event)}>
                <summary>
                  ${this.workLabel}
                  <span class="icon-[material-symbols--keyboard-arrow-down-rounded]"></span>
                </summary>
                <div class="vp-dropdown-panel vp-work-dropdown">
                  ${industryMegaMenu({
                    items: this.workItems,
                    locale: currentLocale,
                    editor,
                    hrefFor: href => this.homeAnchor(href),
                    descriptionFor: item => item.text ?? this.anchorDescription(item.href),
                    onLink: (event, href) => {
                      this.closeAllDropdowns();
                      if (!this.handleEditorLinkClick(event, this.homeAnchor(href))) this.handleHomeAnchorClick(event, href);
                    },
                  })}
                </div>
              </details>

              <details class="vp-nav-dropdown" @toggle=${(event: Event) => this.closeSiblingDropdowns(event)}>
                <summary>
                  ${this.targetGroupLabel}
                  <span class="icon-[material-symbols--keyboard-arrow-down-rounded]"></span>
                </summary>
                <div class="vp-dropdown-panel vp-about-dropdown">
                  ${this.targetItems.map(
                    item => html`
                      <a
                        href=${this.homeAnchor(item.href)}
                        @click=${(event: MouseEvent) =>
                          this.handleEditorLinkClick(event, this.homeAnchor(item.href)) ||
                          this.handleHomeAnchorClick(event, item.href)}
                      >
                        <strong>${item.label}</strong>
                        <span>${item.text ?? this.anchorDescription(item.href)}</span>
                      </a>
                    `,
                  )}
                </div>
              </details>

              <a
                href="/globus"
                @click=${(event: MouseEvent) =>
                  this.handleEditorLinkClick(event, '/globus') || this.closeAllDropdowns()}
                >${this.globusLabel}</a
              >

              <details class="vp-nav-dropdown" @toggle=${(event: Event) => this.closeSiblingDropdowns(event)}>
                <summary>
                  ${this.aboutLabel}
                  <span class="icon-[material-symbols--keyboard-arrow-down-rounded]"></span>
                </summary>
                <div class="vp-dropdown-panel vp-about-dropdown">
                  ${this.aboutItems.map(item =>
                    item.external
                      ? html`
                          <a
                            class="vp-about-item-with-logo"
                            href=${item.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            @click=${() => this.closeAllDropdowns()}
                          >
                            <img class="vp-nav-gvp-logo" src=${item.logo ?? ''} alt="" width="611" height="300" />
                            <strong>${item.label}</strong>
                            <span>${item.text}</span>
                          </a>
                        `
                      : html`
                          <a
                            href=${item.href}
                            @click=${(event: MouseEvent) =>
                              this.handleEditorLinkClick(event, item.href) || this.closeAllDropdowns()}
                          >
                            <strong>${item.label}</strong>
                            <span>${item.text}</span>
                          </a>
                        `,
                  )}
                </div>
              </details>
            </nav>

            ${this.renderPrimaryAction('vp-nav-cta')}

            ${mobileNavigation({
              content, locale: currentLocale, audience: this.activeAudience, industryItems: this.workItems,
              currentPath: this.normalizedCurrentPath, audienceHref: this.currentAudienceHref,
              applicantLabel: this.applicantLabel, companyLabel: this.companyLabel, editor,
              onAudienceChange: event => this.handleAudienceSwitch(event), onLanguageChange,
              hrefFor: href => localizedLandingHref(this.homeAnchor(href), currentLocale, typeof window === 'undefined' ? 'https://www.vivaporta.org' : window.location.origin, this.activeAudience),
              onLink: (event, href) => { this.closeAllDropdowns(); this.handleHomeAnchorClick(event, href); },
              onCompanyContact: event => this.handleCompanyContactClick(event),
            })}
          </div>
        </div>
        ${this.renderContactModal()}
      </header>
    `;
  }

  private renderContactModal() {
    const titleId = 'vp-contact-modal-title';
    return html`
      <dialog
        id="vp-contact-modal"
        class="vp-contact-modal"
        aria-labelledby=${titleId}
        @click=${(event: MouseEvent) => this.handleModalBackdropClick(event)}
        @close=${() => this.handleModalClosed()}
      >
        <div class="vp-contact-modal-card">
          <button
            type="button"
            class="vp-contact-modal-close"
            aria-label=${this.modalCloseLabel}
            @click=${(event: MouseEvent) => this.closeContactModal(event)}
          >
            <span class="icon-[material-symbols--close-rounded]" aria-hidden="true"></span>
          </button>
          <div class="vp-contact-modal-head">
            <span class="vp-contact-modal-eyebrow">${this.modalEyebrow}</span>
            <h2 id=${titleId} class="vp-contact-modal-title">${this.modalTitle}</h2>
            <p class="vp-contact-modal-lead">${this.modalLead}</p>
          </div>
          <div class="vp-contact-modal-options">
            <a class="vp-contact-option" href="mailto:${companyContact.email}">
              <span class="vp-contact-option-icon"
                ><span class="icon-[material-symbols--mail-rounded]" aria-hidden="true"></span
              ></span>
              <span class="vp-contact-option-body">
                <span class="vp-contact-option-label">${this.modalEmailLabel}</span>
                <span class="vp-contact-option-value">${companyContact.email}</span>
              </span>
              <span
                class="vp-contact-option-go icon-[material-symbols--arrow-outward-rounded]"
                aria-hidden="true"
              ></span>
            </a>
            <a class="vp-contact-option" href=${companyContact.phoneHref}>
              <span class="vp-contact-option-icon"
                ><span class="icon-[material-symbols--call]" aria-hidden="true"></span
              ></span>
              <span class="vp-contact-option-body">
                <span class="vp-contact-option-label">${this.modalPhoneLabel}</span>
                <span class="vp-contact-option-value">${companyContact.phone}</span>
              </span>
              <span
                class="vp-contact-option-go icon-[material-symbols--arrow-outward-rounded]"
                aria-hidden="true"
              ></span>
            </a>
          </div>
        </div>
      </dialog>
    `;
  }

  private renderPrimaryAction(className: string) {
    const action = this.primaryNavAction;
    if (this.activeAudience === 'company') {
      return html`<button
        type="button"
        class=${className}
        aria-haspopup="dialog"
        aria-controls="vp-contact-modal"
        @click=${(event: MouseEvent) => this.handleCompanyContactClick(event)}
      >
        ${action.label}
      </button>`;
    }
    return html`<a
      class=${className}
      href=${action.href}
      @click=${(event: MouseEvent) => this.handleEditorLinkClick(event, action.href)}
      >${action.label}</a
    >`;
  }

  private get aboutLabel() {
    if (this.options.currentLocale === 'tr') return 'Viva Porta hakkında';
    if (this.options.currentLocale === 'en') return 'About Viva Porta';
    return 'Über Viva Porta';
  }

  private get workLabel() {
    if (this.options.currentLocale === 'tr') return 'Sektörler';
    if (this.options.currentLocale === 'en') return 'Industries';
    return 'Branchen';
  }

  private get targetGroupLabel() {
    if (this.activeAudience === 'company') {
      if (this.options.currentLocale === 'tr') return 'Uzman kazanımı';
      if (this.options.currentLocale === 'en') return 'Hire specialists';
      return 'Fachkräfte gewinnen';
    }

    if (this.options.currentLocale === 'tr') return 'Almanya’da çalışmak';
    if (this.options.currentLocale === 'en') return 'Working in Germany';
    return 'Arbeiten in Deutschland';
  }

  private get globusLabel() {
    if (this.options.currentLocale === 'tr') return 'Globus';
    if (this.options.currentLocale === 'en') return 'Globus';
    return 'Globus';
  }

  private get primaryNavAction() {
    if (this.activeAudience === 'company') {
      return {
        label:
          this.options.currentLocale === 'tr'
            ? 'İletişime geç'
            : this.options.currentLocale === 'en'
              ? 'Request specialists'
              : 'Fachkräfte anfragen',
        href: '/kontakt',
      };
    }

    return {
      label:
        this.options.currentLocale === 'tr' ? 'Başvur' : this.options.currentLocale === 'en' ? 'Apply' : 'Bewerben',
      href: applicationPortal.overview,
    };
  }

  private get applicantLabel() {
    if (this.options.currentLocale === 'tr') return 'Adaylar';
    if (this.options.currentLocale === 'en') return 'Applicants';
    return 'Bewerber';
  }

  private get companyLabel() {
    if (this.options.currentLocale === 'tr') return 'Şirketler';
    if (this.options.currentLocale === 'en') return 'Companies';
    return 'Unternehmen';
  }

  private get audienceLabel() {
    if (this.options.currentLocale === 'tr') return 'Hedef kitle değiştir';
    if (this.options.currentLocale === 'en') return 'Switch audience';
    return 'Zielgruppe wechseln';
  }

  private get currentPath() {
    if (this.options.editor?.mode === 'editor' && this.options.editor.currentPath)
      return this.options.editor.currentPath;
    if (typeof window === 'undefined') return '/';
    return window.location.pathname;
  }

  private audienceClass(audience: StaticPageAudience) {
    return this.activeAudience === audience ? 'is-active' : '';
  }

  private handleAudienceSwitch(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    const activeLink = event.currentTarget as HTMLAnchorElement;
    const nextAudience = activeLink.dataset.audience === 'company' ? 'company' : 'applicant';
    this.persistAudience(nextAudience);
    this.options.onAudienceChange?.(nextAudience);
    document
      .querySelectorAll<HTMLAnchorElement>('.vp-audience-switch a, .vp-mobile-audience-switch a')
      .forEach(link => {
        link.classList.toggle('is-active', link.dataset.audience === nextAudience);
      });
  }

  private get activeAudience(): StaticPageAudience {
    return this.options.audience ?? this.storedAudience;
  }

  private get normalizedCurrentPath() {
    return this.normalizePath(this.currentPath);
  }

  private get currentAudienceHref() {
    return this.normalizedCurrentPath || '/';
  }

  private get storedAudience(): StaticPageAudience {
    if (typeof window === 'undefined') return 'applicant';
    return window.localStorage.getItem(LandingNavigationViewModel.audienceStorageKey) === 'company'
      ? 'company'
      : 'applicant';
  }

  private persistAudience(audience: StaticPageAudience) {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(LandingNavigationViewModel.audienceStorageKey, audience);
  }

  private normalizePath(path: string) {
    return path.length > 1 ? path.replace(/\/$/, '') : path;
  }

  private handleEditorLinkClick(event: MouseEvent, href: string) {
    if (this.options.editor?.mode !== 'editor') return false;
    event.preventDefault();
    event.stopPropagation();
    this.closeAllDropdowns();
    this.options.editor.onNavigate?.(href);
    return true;
  }

  // The company CTA is a real button, so every page opens its local navigation dialog without a
  // route transition. The applicant CTA remains a normal link to the application portal.
  private handleCompanyContactClick(event: MouseEvent) {
    if (this.handleEditorLinkClick(event, this.primaryNavAction.href)) return;
    const navigation = (event.currentTarget as HTMLElement).closest<HTMLElement>('.vp-navigation');
    this.closeAllDropdowns();
    this.closeMobileMenu();
    openCompanyContactDialog(navigation);
  }

  private closeContactModal(event?: Event) {
    const fromTarget = (event?.target as HTMLElement | undefined)?.closest?.('dialog') as HTMLDialogElement | null;
    const dialog = fromTarget ?? document.querySelector<HTMLDialogElement>('.vp-contact-modal');
    if (!dialog) return;
    if (typeof dialog.close === 'function') dialog.close();
    else {
      dialog.removeAttribute('open');
      this.handleModalClosed();
    }
  }

  // Fired natively on close() and on ESC — release the background scroll lock.
  private handleModalClosed() {
    document.querySelector('.vp-page')?.classList.remove('vp-scroll-locked');
  }

  private handleModalBackdropClick(event: MouseEvent) {
    if (event.target === event.currentTarget) this.closeContactModal(event);
  }

  private closeMobileMenu() {
    closeMobileNavigation();
    document.querySelectorAll<HTMLDetailsElement>('.vp-mobile-menu[open]').forEach(menu => {
      menu.open = false;
    });
  }

  private get modalEyebrow() {
    if (this.options.currentLocale === 'tr') return 'Doğrudan iletişim';
    if (this.options.currentLocale === 'en') return 'Direct contact';
    return 'Direkter Draht';
  }

  private get modalTitle() {
    if (this.options.currentLocale === 'tr') return 'Uzman talep edin';
    if (this.options.currentLocale === 'en') return 'Request specialists';
    return 'Fachkräfte anfragen';
  }

  private get modalLead() {
    if (this.options.currentLocale === 'tr')
      return 'Size en uygun yoldan ulaşın – e-posta veya telefon. Genellikle aynı gün yanıt veriyoruz.';
    if (this.options.currentLocale === 'en')
      return 'Reach us the way that suits you best — by email or phone. We usually reply the same day.';
    return 'Erreichen Sie uns auf dem Weg, der für Sie am besten passt – per E-Mail oder telefonisch. Wir antworten meist noch am selben Tag.';
  }

  private get modalEmailLabel() {
    if (this.options.currentLocale === 'tr') return 'E-posta';
    if (this.options.currentLocale === 'en') return 'Email';
    return 'E-Mail';
  }

  private get modalPhoneLabel() {
    if (this.options.currentLocale === 'tr') return 'Telefon';
    if (this.options.currentLocale === 'en') return 'Phone';
    return 'Telefon';
  }

  private get modalCloseLabel() {
    if (this.options.currentLocale === 'tr') return 'Kapat';
    if (this.options.currentLocale === 'en') return 'Close';
    return 'Schließen';
  }

  private closeSiblingDropdowns(event: Event) {
    const activeDropdown = event.currentTarget as HTMLDetailsElement;
    if (!activeDropdown.open) {
      this.unregisterOutsideClick();
      return;
    }

    activeDropdown
      .closest('.vp-nav-links')
      ?.querySelectorAll<HTMLDetailsElement>('.vp-nav-dropdown[open]')
      .forEach(dropdown => {
        if (dropdown !== activeDropdown) dropdown.open = false;
      });

    this.registerOutsideClick(activeDropdown);
  }

  private handleHomeAnchorClick(event: MouseEvent, href: string) {
    if (!href.startsWith('#')) return;

    event.preventDefault();
    this.closeAllDropdowns();

    if (this.currentPath !== '/') {
      window.location.href = localizedLandingHref(`/${href}`, this.options.currentLocale, window.location.origin, this.activeAudience);
      return;
    }

    this.scrollToAnchor(href);
  }

  private scrollToAnchor(href: string) {
    const target = document.querySelector<HTMLElement>(href);
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.history.replaceState(window.history.state, '', new URL(href, window.location.href));
  }

  private closeAllDropdowns() {
    document.querySelectorAll<HTMLDetailsElement>('.vp-nav-dropdown[open]').forEach(dropdown => {
      dropdown.open = false;
    });
    this.unregisterOutsideClick();
  }

  private registerOutsideClick(activeDropdown: HTMLDetailsElement) {
    this.unregisterOutsideClick();

    const closeOnOutsideClick = (event: PointerEvent) => {
      if (event.target instanceof Node && activeDropdown.contains(event.target)) return;
      this.closeAllDropdowns();
    };

    document.addEventListener('pointerdown', closeOnOutsideClick);
    LandingNavigationViewModel.outsideClickCleanup = () =>
      document.removeEventListener('pointerdown', closeOnOutsideClick);
  }

  private unregisterOutsideClick() {
    LandingNavigationViewModel.outsideClickCleanup?.();
    LandingNavigationViewModel.outsideClickCleanup = undefined;
  }

  private get aboutItems() {
    return navigationAboutItems(this.options.currentLocale);
  }

  private get targetItems(): NavigationWorkItem[] {
    if (this.activeAudience === 'company') {
      if (this.options.currentLocale === 'tr') {
        return [
          { label: 'Süreç', href: '#ablauf', text: 'İhtiyaçtan işe başlangıca kadar' },
          { label: 'Kalite', href: '#qualitaet', text: 'Ön seçim, uygunluk ve entegrasyon' },
          { label: 'İletişim', href: '#kontakt', text: 'Personel ihtiyacınızı görüşün' },
        ];
      }

      if (this.options.currentLocale === 'en') {
        return [
          { label: 'Process', href: '#ablauf', text: 'From demand to arrival in the team' },
          { label: 'Quality', href: '#qualitaet', text: 'Pre-selection, fit and integration' },
          { label: 'Contact', href: '#kontakt', text: 'Discuss your staffing needs' },
        ];
      }

      return [
        { label: 'Ablauf', href: '#ablauf', text: 'Vom Bedarf bis zum Ankommen im Team' },
        { label: 'Qualität', href: '#qualitaet', text: 'Vorauswahl, Passung und Integration' },
        { label: 'Kontakt', href: '#kontakt', text: 'Personalbedarf direkt besprechen' },
      ];
    }

    if (this.options.currentLocale === 'tr') {
      return [
        { label: 'Yol', href: '#weg', text: this.anchorDescription('#weg') },
        { label: 'Sektörler', href: '#branchen', text: this.anchorDescription('#branchen') },
        { label: 'Süreç', href: '#ablauf', text: this.anchorDescription('#ablauf') },
        { label: 'İletişim', href: '#kontakt', text: this.anchorDescription('#kontakt') },
      ];
    }

    if (this.options.currentLocale === 'en') {
      return [
        { label: 'Path', href: '#weg', text: this.anchorDescription('#weg') },
        { label: 'Industries', href: '#branchen', text: this.anchorDescription('#branchen') },
        { label: 'Process', href: '#ablauf', text: this.anchorDescription('#ablauf') },
        { label: 'Contact', href: '#kontakt', text: this.anchorDescription('#kontakt') },
      ];
    }

    return [
      { label: 'Weg', href: '#weg', text: this.anchorDescription('#weg') },
      { label: 'Branchen', href: '#branchen', text: this.anchorDescription('#branchen') },
      { label: 'Ablauf', href: '#ablauf', text: this.anchorDescription('#ablauf') },
      { label: 'Kontakt', href: '#kontakt', text: this.anchorDescription('#kontakt') },
    ];
  }

  private get workItems(): NavigationWorkItem[] {
    return this.options.content.industryNav ?? industryNavigationItems(this.options.currentLocale);
  }

  private homeAnchor(href: string) {
    return href.startsWith('#') ? `/${href}` : href;
  }

  private anchorDescription(href: string) {
    if (this.options.currentLocale === 'tr') {
      const trDescriptions: Record<string, string> = {
        '/branchen/pflege': 'Bakım tesisleri için çözüm',
        '/branchen/handwerk': 'Zanaat işletmeleri için çözüm',
        '/branchen/einzelhandel': 'Globus ve perakende için çözüm',
        '#weg': 'Başlangıç, şartlar ve yönlendirme',
        '#branchen': 'Uygun çalışma alanlarını keşfet',
        '#ablauf': 'Başvurudan işe başlangıca kadar',
        '#vorteile': 'Destek, dil ve entegrasyon',
        '#kosten': 'Adaylar için şeffaf maliyetler',
        '#anerkennung': 'Dil, belgeler ve mesleki denklik',
        '#qualitaet': 'Ön seçim, uyum ve entegrasyon',
        '#kontakt': 'Doğrudan danışmanlık ve sonraki adımlar',
        '#informationen': 'Resmi kaynaklar ve ek bilgiler',
      };

      return trDescriptions[href] ?? 'Bölüme git';
    }

    if (this.options.currentLocale === 'en') {
      const enDescriptions: Record<string, string> = {
        '/branchen/pflege': 'Solution for care providers',
        '/branchen/handwerk': 'Solution for skilled trades',
        '/branchen/einzelhandel': 'Solution for Globus and retail teams',
        '#weg': 'Orientation, requirements and first steps',
        '#branchen': 'Explore suitable fields of work',
        '#ablauf': 'From application to starting work',
        '#vorteile': 'Support, language and integration',
        '#kosten': 'Transparent costs for applicants',
        '#anerkennung': 'Language, documents and recognition',
        '#qualitaet': 'Pre-selection, fit and integration',
        '#kontakt': 'Direct advice and next steps',
        '#informationen': 'Official resources and additional information',
      };

      return enDescriptions[href] ?? 'Go to section';
    }

    const descriptions: Record<string, string> = {
      '/branchen/pflege': 'Lösung für Pflegeeinrichtungen',
      '/branchen/handwerk': 'Lösung für Handwerksbetriebe',
      '/branchen/einzelhandel': 'Lösung für Globus und Einzelhandel',
      '#weg': 'Orientierung, Voraussetzungen und Start',
      '#branchen': 'Passende Arbeitsbereiche entdecken',
      '#ablauf': 'Von Bewerbung bis Arbeitsstart',
      '#vorteile': 'Begleitung, Sprache und Integration',
      '#kosten': 'Transparenz für Bewerberinnen und Bewerber',
      '#anerkennung': 'Sprache, Unterlagen und Berufsanerkennung',
      '#qualitaet': 'Vorauswahl, Passung und Integration',
      '#kontakt': 'Direkte Beratung und nächste Schritte',
      '#informationen': 'Weiterführende offizielle Informationen',
    };

    return descriptions[href] ?? 'Direkt zum Abschnitt';
  }
}

export const landingNavigation = (options: NavigationOptions) => new LandingNavigationViewModel(options).render();
